/* FP Blog Grid Infinite (merge pretty public tags + landing tags, with correct tile pattern)
   Restored from the original working version, with two safety additions:
   - a failed landing_ feed cannot stop the main feed
   - after appending, it checks whether the sentinel is still close and keeps filling smoothly

   Behaviour:
   - /blogs/field-notes => normal infinite scroll
   - /blogs/field-notes/tagged/<tag> => merges <tag> + landing_<tag> (deduped)
   - Pretty URL cases: polar -> landing_polar, ultrarunning/running -> landing_ultrarunning
   - After every append: re-applies the SAME 11-step tile pattern used in Liquid
*/

(() => {
  function init() {
    const root = document.querySelector('.fp-blog');
    if (!root) return;

    const gridEl = root.querySelector('.fp-blog-grid');
    const meta = root.querySelector('.fp-blog-infinite');
    const sentinel = root.querySelector('.fp-blog-sentinel');
    if (!gridEl || !meta || !sentinel) return;

    // ---------- kill legacy debug UI (if present) ----------
    root.querySelectorAll('.fp-blog-infodebug, [data-fp-infodebug], .fp-blog-loadmore, [data-fp-loadmore]').forEach((el) => {
      try { el.remove(); } catch(e) {}
    });

    // ---------- dedupe ----------
    const seen = new Set();
    const keyFor = (tile) => {
      const a = tile.querySelector('a[href]');
      return a ? a.getAttribute('href') : tile.outerHTML.slice(0, 240);
    };

    function registerExisting() {
      gridEl.querySelectorAll('.fp-blog-tile').forEach((t) => seen.add(keyFor(t)));
    }
    registerExisting();

    // ---------- tile pattern (must match Liquid) ----------
    function applyTilePattern() {
      const tiles = Array.from(gridEl.querySelectorAll('.fp-blog-tile'));

      tiles.forEach((tile, i) => {
        tile.classList.remove(
          'fp-blog-tile--hero',
          'fp-blog-tile--large',
          'fp-blog-tile--medium',
          'fp-blog-tile--small'
        );

        const mod = i % 11;
        let cls = 'fp-blog-tile--small';
        if (mod === 0) cls = 'fp-blog-tile--hero';
        else if (mod === 1) cls = 'fp-blog-tile--large';
        else if (mod === 2 || mod === 3) cls = 'fp-blog-tile--medium';

        tile.classList.add(cls);
      });
    }

    function normaliseImageLoading() {
      const tiles = Array.from(gridEl.querySelectorAll('.fp-blog-tile'));
      tiles.forEach((tile, i) => {
        tile.querySelectorAll('img').forEach((img) => {
          if (i === 0) {
            img.setAttribute('loading', 'eager');
            img.setAttribute('fetchpriority', 'high');
          } else {
            img.setAttribute('loading', 'lazy');
            img.setAttribute('decoding', 'async');
            img.removeAttribute('fetchpriority');
          }
        });
      });
    }

    function refreshLayout() {
      applyTilePattern();
      normaliseImageLoading();
    }

    // Ensure initial server-rendered tiles also match pattern
    refreshLayout();

    // ---------- detect current tag from URL ----------
    const path = window.location.pathname || '';
    const m = path.match(/\/tagged\/([^\/\?]+)/);
    const mainTag = m ? decodeURIComponent(m[1]) : '';
    const isLandingPage = !!(mainTag && mainTag.toLowerCase().startsWith('landing_'));

    // Landing-stream mapping.
    // Most visible category URLs can merge with landing_<tag>.
    // These two are intentionally pretty public URLs, but their real article tags are landing_* only.
    const prettyLandingMap = {
      ultrarunning: 'landing_ultrarunning',
      polar: 'landing_polar',
      running: 'landing_ultrarunning'
    };

    let landingTag = '';
    let isPrettyLandingProxy = false;

    if (mainTag && !isLandingPage) {
      if (Object.prototype.hasOwnProperty.call(prettyLandingMap, mainTag)) {
        landingTag = prettyLandingMap[mainTag];
        isPrettyLandingProxy = (mainTag === 'ultrarunning' || mainTag === 'polar');
      } else {
        landingTag = `landing_${mainTag}`;
      }
    }

    let nextUrlMain = (meta.getAttribute('data-fp-next-url') || '').trim();

    // For pretty proxy pages, do not waste a request on the empty public tag.
    if (isPrettyLandingProxy) nextUrlMain = '';

    // Only use landing stream if we're on a non-landing tagged page
    let nextUrlLanding = landingTag
      ? `/blogs/field-notes/tagged/${encodeURIComponent(landingTag)}`
      : '';

    let loading = false;
    let prefillTimer = null;

    async function fetchAndAppend(url) {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`fetch ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.fp-blog-grid');
      if (!newGrid) return { appended: 0, nextUrl: '' };

      const items = Array.from(newGrid.querySelectorAll('.fp-blog-tile'));
      let appended = 0;

      items.forEach((item) => {
        const k = keyFor(item);
        if (seen.has(k)) return;

        seen.add(k);
        gridEl.appendChild(item);
        appended++;
      });

      const newMeta = doc.querySelector('.fp-blog-infinite');
      const nextUrl = (newMeta && newMeta.getAttribute('data-fp-next-url') || '').trim();

      return { appended, nextUrl };
    }

    async function safeFetch(which, url) {
      try {
        const r = await fetchAndAppend(url);
        return { which, ok: true, ...r };
      } catch (e) {
        return { which, ok: false, appended: 0, nextUrl: '' };
      }
    }

    function sentinelIsNear() {
      const rect = sentinel.getBoundingClientRect();
      return rect.top < (window.innerHeight + 900);
    }

    function schedulePrefill() {
      window.clearTimeout(prefillTimer);
      prefillTimer = window.setTimeout(() => {
        if (!loading && (nextUrlMain || nextUrlLanding) && sentinelIsNear()) {
          loadNext();
        }
      }, 120);
    }

    async function loadNext() {
      if (loading) return;
      if (!nextUrlMain && !nextUrlLanding) return;

      loading = true;
      try {
        // ALL CATEGORIES: only main feed
        if (!mainTag) {
          if (!nextUrlMain) return;
          const r = await safeFetch('main', nextUrlMain);
          nextUrlMain = r.ok ? r.nextUrl : '';
          refreshLayout();
          return;
        }

        // TAGGED: merge both feeds (OR logic). Each feed is isolated so one empty/erroring
        // stream cannot kill the other stream.
        const jobs = [];
        if (nextUrlMain) jobs.push(safeFetch('main', nextUrlMain));
        if (nextUrlLanding) jobs.push(safeFetch('landing', nextUrlLanding));

        const results = await Promise.all(jobs);

        results.forEach((r) => {
          if (r.which === 'main') nextUrlMain = r.ok ? r.nextUrl : '';
          if (r.which === 'landing') nextUrlLanding = r.ok ? r.nextUrl : '';
        });

        refreshLayout();
      } finally {
        loading = false;
        schedulePrefill();
      }
    }

    // ---------- infinite scroll ----------
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadNext();
      },
      { rootMargin: '900px 0px' }
    );

    io.observe(sentinel);

    // Kick once so landing feed page 1 is merged quickly on tagged pages.
    // Also useful on very tall screens where the sentinel is already near view.
    setTimeout(loadNext, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
